<template>
  <CTabs variant="tabs" class="nav-tabs-boxed">
    <CTab :title="descPlural">
      <div class="conteudo-listagem">
        <Loading :isActive="loading" />

        <ConteudoVisualFormModal :item="item" :close="closeEdit" :logosTipo="logosTipo" />

        <div class="conteudo-listagem__header">
          <div>
            <h4 class="conteudo-listagem__title">{{ descPlural }}</h4>
            <p class="conteudo-listagem__subtitle">
              Imagens e blocos de texto da página inicial e do rodapé do VersoCommerce e do VersoApp.
            </p>
          </div>
          <CButton color="primary" @click="openCreate">
            <CIcon name="cil-plus" size="sm" class="mr-1" />
            Novo conteúdo
          </CButton>
        </div>

        <StatusFilterBar v-model="filtroLocal" :chips="chipsLocais" :contagens="contagem" />

        <div class="conteudo-listagem__filtros">
          <CRow>
            <CCol md="6">
              <div class="filtro-search-wrapper">
                <CIcon name="cil-magnifying-glass" size="sm" class="filtro-search-icon" />
                <input
                  class="form-control filtro-search-input"
                  type="text"
                  v-model="filtros.termo"
                  placeholder="Buscar por descrição, título ou link..."
                />
              </div>
            </CCol>
            <CCol md="2">
              <select class="form-control" v-model="filtros.conteudo">
                <option value="">Todo conteúdo</option>
                <option value="imagem">Imagem</option>
                <option value="texto">Bloco de texto</option>
                <option value="HTML">HTML</option>
              </select>
            </CCol>
            <CCol md="2">
              <select class="form-control" v-model="filtros.canal">
                <option value="">Todas as plataformas</option>
                <option v-for="canal in canais" :key="canal.campo" :value="canal.campo">
                  {{ canal.label }}
                </option>
              </select>
            </CCol>
            <CCol md="2">
              <select class="form-control" v-model="filtros.status">
                <option value="">Todos os status</option>
                <option value="ATIVO">Ativos</option>
                <option value="INATIVO">Inativos</option>
              </select>
            </CCol>
          </CRow>
        </div>

        <div class="conteudo-listagem__table-wrapper">
          <table class="conteudo-listagem__table">
            <thead>
              <tr>
                <th class="col-previa">Prévia</th>
                <th>Descrição</th>
                <th class="col-local">Local</th>
                <th class="col-conteudo">Conteúdo</th>
                <th class="col-largura">Largura</th>
                <th class="col-canais">Plataformas</th>
                <th class="col-ordem">Ordem</th>
                <th class="col-status">Status</th>
                <th class="col-acoes"></th>
              </tr>
            </thead>
            <tbody>
              <tr v-if="conteudosFiltrados.length === 0">
                <td colspan="9" class="conteudo-listagem__empty">
                  <CIcon name="cil-ban" size="xl" class="conteudo-listagem__empty-icon" />
                  <p>{{ items.length === 0 ? "Nenhum conteúdo cadastrado" : "Nenhum conteúdo encontrado com esses filtros" }}</p>
                </td>
              </tr>
              <tr
                v-for="conteudo in conteudosFiltrados"
                :key="conteudo.id"
                class="conteudo-listagem__row"
                :class="{ 'conteudo-listagem__row--inativo': !ativo(conteudo) }"
              >
                <td class="col-previa">
                  <PreviewBlocoTexto
                    v-if="getTipoConteudo(conteudo).chave === 'texto'"
                    :item="conteudo"
                    compacto
                  />
                  <span
                    v-else-if="getTipoConteudo(conteudo).chave === 'HTML'"
                    class="conteudo-listagem__previa-html"
                  >
                    <CIcon name="cil-code" />
                  </span>
                  <img
                    v-else-if="conteudo.imagem || conteudo.imagem_mobile"
                    :src="conteudo.imagem || conteudo.imagem_mobile"
                    class="conteudo-listagem__thumb"
                    loading="lazy"
                    decoding="async"
                  />
                  <span v-else class="conteudo-listagem__sem-imagem">—</span>
                </td>
                <td>
                  <span class="conteudo-listagem__nome">{{ conteudo.descricao }}</span>
                  <span v-if="conteudo.titulo && getTipoConteudo(conteudo).chave === 'texto'" class="conteudo-listagem__detalhe">
                    {{ conteudo.titulo }}
                  </span>
                  <span v-else-if="conteudo.link" class="conteudo-listagem__detalhe">
                    <fa-icon icon="link" class="mr-1" />{{ conteudo.link }}
                  </span>
                </td>
                <td class="col-local">
                  <span class="conteudo-listagem__label">
                    <CIcon :name="localDoConteudo(conteudo).icon" size="sm" class="mr-1" />
                    {{ localDoConteudo(conteudo).label }}
                  </span>
                </td>
                <td class="col-conteudo">
                  <span
                    class="conteudo-listagem__tipo"
                    :class="`conteudo-listagem__tipo--${getTipoConteudo(conteudo).chave.toLowerCase()}`"
                  >
                    {{ getTipoConteudo(conteudo).label }}
                  </span>
                </td>
                <td class="col-largura">{{ larguraDoConteudo(conteudo) }}</td>
                <td class="col-canais">
                  <span
                    v-for="canal in canaisDoConteudo(conteudo)"
                    :key="canal.campo"
                    class="conteudo-listagem__canal"
                    :title="canal.label"
                  >
                    {{ canal.sigla }}
                  </span>
                  <span v-if="canaisDoConteudo(conteudo).length === 0" class="conteudo-listagem__sem-imagem">—</span>
                </td>
                <td class="col-ordem">{{ conteudo.ordem }}</td>
                <td class="col-status">
                  <span
                    class="conteudo-listagem__status"
                    :style="{ backgroundColor: statusDoConteudo(conteudo).badgeColor }"
                  >
                    {{ statusDoConteudo(conteudo).label }}
                  </span>
                </td>
                <td class="actions">
                  <CButton size="sm" color="secondary" title="Duplicar" @click="cloneConteudo(conteudo)">
                    <fa-icon icon="clone" />
                  </CButton>
                  <CButton size="sm" color="primary" title="Alterar" @click="editConteudo(conteudo)">
                    <fa-icon icon="pencil-alt" />
                  </CButton>
                  <CButton size="sm" color="danger" title="Excluir" @click="deleteConfirm(conteudo.id)">
                    <fa-icon icon="trash-alt" />
                  </CButton>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </CTab>
  </CTabs>
</template>

<script>
import { get, del } from "@/helpers/apiRequest";
import Loading from "@/components/Loading.vue";
import StatusFilterBar from "@/components/StatusFilterBar.vue";
import ConteudoVisualFormModal from "./ConteudoVisualFormPage.vue";
import PreviewBlocoTexto from "../shared/PreviewBlocoTexto.vue";
import { itemNovo, normalizarItemParaEdicao, ehTipoSlotHome } from "../helpers/blocoConteudo";
import {
  CANAIS,
  canaisDoConteudo,
  cloneParaCriacao,
  contarPorLocal,
  filtrarConteudos,
  getLarguraPercentual,
  getLocalConteudo,
  getTipoConteudo,
  locaisOrdenados,
  statusDoConteudo,
} from "../helpers/conteudoLabels";

export default {
  name: "ConteudoVisualListagem",
  components: {
    Loading,
    StatusFilterBar,
    ConteudoVisualFormModal,
    PreviewBlocoTexto,
  },
  data() {
    return {
      loading: false,
      descPlural: "Conteúdos visuais",
      item: itemNovo(),
      items: [],
      logosTipo: [],
      canais: CANAIS,
      filtros: {
        termo: "",
        local: "",
        conteudo: "",
        canal: "",
        status: "",
      },
    };
  },
  computed: {
    locais() {
      return locaisOrdenados(this.logosTipo);
    },
    chipsLocais() {
      return [
        { chave: "todos", label: "Todos os locais" },
        ...this.locais.map((local) => ({ chave: local.chave, label: local.label })),
      ];
    },
    contagem() {
      return { todos: this.items.length, ...contarPorLocal(this.items, this.logosTipo) };
    },
    filtroLocal: {
      get() {
        return this.filtros.local || "todos";
      },
      set(chave) {
        this.filtros.local = chave === "todos" ? "" : chave;
      },
    },
    conteudosFiltrados() {
      return filtrarConteudos(this.items, this.filtros, this.logosTipo);
    },
  },
  methods: {
    canaisDoConteudo,
    getTipoConteudo,
    statusDoConteudo,
    ativo(conteudo) {
      return Number(conteudo.ativo) === 1;
    },
    localDoConteudo(conteudo) {
      return getLocalConteudo(conteudo.chave, this.logosTipo);
    },
    larguraDoConteudo(conteudo) {
      return ehTipoSlotHome(conteudo) ? getLarguraPercentual(conteudo.largura) : "—";
    },
    loadDescricaoModulo() {
      const { modulos } = JSON.parse(localStorage.getItem("app"));
      const modulo = modulos.find((m) => m.nome === "ADMIN_CONTEUDO_VISUAL");

      if (modulo) {
        this.descPlural = modulo.descPlural;
      }
    },
    loadConteudos() {
      this.loading = true;

      get("/admin/site-logos")
        .then((conteudos) => {
          this.items = conteudos;
        })
        .catch((err) => {
          this.$swal({
            title: "Falha ao carregar os conteúdos",
            text: err.message,
            icon: "error",
          });
        })
        .finally(() => {
          this.loading = false;
        });
    },
    loadLocais() {
      get("/admin/site-logos-tipo")
        .then((tipos) => {
          this.logosTipo = tipos;
        })
        .catch((err) => {
          this.$swal({
            title: "Falha ao carregar os locais de exibição",
            text: err.message,
            icon: "error",
          });
        });
    },
    abrirFormulario(item) {
      this.item = normalizarItemParaEdicao(item);
      this.$modal.show("conteudoVisualEdit");
    },
    openCreate() {
      const local = this.locais.find((opcao) => opcao.chave === this.filtros.local) || this.locais[0];

      if (!local) {
        this.$swal({
          title: "Locais de exibição indisponíveis",
          text: "Não foi possível carregar os locais de exibição. Recarregue a página e tente novamente.",
          icon: "warning",
        });
        return;
      }

      this.abrirFormulario({
        ...itemNovo(),
        tipo_id: local.id,
        chave: local.chave,
        acesso_site: 1,
        acesso_app: 1,
      });
    },
    editConteudo(conteudo) {
      this.abrirFormulario(conteudo);
    },
    cloneConteudo(conteudo) {
      this.abrirFormulario(cloneParaCriacao(conteudo));
    },
    closeEdit() {
      this.$modal.hide("conteudoVisualEdit");
      this.loadConteudos();
    },
    deleteConfirm(id) {
      this.$swal({
        title: "Confirma Remover?",
        text: "Essa ação não poderá ser desfeita",
        showCancelButton: true,
        icon: "warning",
        confirmButtonText: "Excluir",
        cancelButtonText: "Cancelar",
      })
        .then((res) => {
          if (res.value) {
            this.loading = true;
            del(`/admin/site-logos/${id}`)
              .then(() => {
                this.loadConteudos();
                this.$swal({
                  title: "Removido com sucesso",
                  icon: "success",
                });
              })
              .catch(() => {
                this.loading = false;
                this.$swal({
                  title: "Falha ao remover",
                  icon: "error",
                });
              });
          }
        })
        .catch(() => {});
    },
  },
  beforeMount() {
    this.loadDescricaoModulo();
    this.loadConteudos();
    this.loadLocais();
  },
};
</script>

<style scoped>
.conteudo-listagem__header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 16px;
  margin-bottom: 16px;
}

.conteudo-listagem__title {
  margin: 0;
  font-size: 18px;
  font-weight: 600;
  color: #2c2c2c;
}

.conteudo-listagem__subtitle {
  margin: 2px 0 0;
  font-size: 13px;
  color: #667085;
}

.conteudo-listagem__filtros {
  margin-bottom: 20px;
  padding: 16px;
  background: #f9fafb;
  border: 1px solid #e4e7eb;
  border-radius: 10px;
}

.conteudo-listagem__filtros .form-control {
  font-size: 13px;
  border-radius: 6px;
  border: 1px solid #dddce9;
}

.conteudo-listagem__filtros .form-control:focus {
  border-color: #3a3e81;
  box-shadow: 0 0 0 2px rgba(58, 62, 129, 0.1);
}

.filtro-search-wrapper {
  position: relative;
}

.filtro-search-icon {
  position: absolute;
  left: 12px;
  top: 50%;
  transform: translateY(-50%);
  color: #98a2b3;
  z-index: 1;
}

.filtro-search-input {
  padding-left: 36px !important;
}

.conteudo-listagem__table-wrapper {
  border: 1px solid #e4e7eb;
  border-radius: 10px;
  overflow-x: auto;
  background: #fff;
  margin-bottom: 20px;
}

.conteudo-listagem__table {
  width: 100%;
  border-collapse: collapse;
}

.conteudo-listagem__table thead tr {
  background: #f9fafb;
  border-bottom: 1px solid #e4e7eb;
}

.conteudo-listagem__table th {
  font-size: 11px;
  font-weight: 600;
  color: #667085;
  text-transform: uppercase;
  letter-spacing: 0.3px;
  padding: 12px;
  white-space: nowrap;
}

.conteudo-listagem__table td {
  padding: 12px;
  border-bottom: 1px solid #f2f4f7;
  font-size: 13px;
  color: #344054;
  vertical-align: middle;
}

.conteudo-listagem__row:last-child td {
  border-bottom: none;
}

.conteudo-listagem__row:hover {
  background: #f9fafb;
}

.conteudo-listagem__row--inativo .col-previa {
  opacity: 0.5;
}

.col-previa { width: 110px; text-align: center; }
.col-local { width: 150px; }
.col-conteudo { width: 120px; text-align: center; }
.col-largura { width: 80px; text-align: center; }
.col-canais { width: 170px; }
.col-ordem { width: 70px; text-align: center; }
.col-status { width: 90px; text-align: center; }
.col-acoes { width: 120px; }

.conteudo-listagem__thumb {
  max-width: 90px;
  max-height: 40px;
  border-radius: 4px;
  border: 1px solid #e4e7eb;
  object-fit: cover;
}

.conteudo-listagem__previa-html {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 90px;
  height: 40px;
  border-radius: 4px;
  background: #f2f4f7;
  color: #667085;
}

.conteudo-listagem__sem-imagem {
  color: #d0d5dd;
}

.conteudo-listagem__nome {
  display: block;
  font-weight: 500;
  color: #1d2939;
}

.conteudo-listagem__detalhe {
  display: block;
  max-width: 320px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-size: 12px;
  color: #98a2b3;
}

.conteudo-listagem__label {
  display: inline-flex;
  align-items: center;
  font-size: 12px;
  color: #555;
}

.conteudo-listagem__tipo {
  display: inline-block;
  padding: 2px 8px;
  border-radius: 6px;
  font-size: 11px;
  font-weight: 600;
  white-space: nowrap;
}

.conteudo-listagem__tipo--imagem {
  background: #eff8ff;
  color: #175cd3;
}

.conteudo-listagem__tipo--texto {
  background: #f4f3ff;
  color: #5925dc;
}

.conteudo-listagem__tipo--html {
  background: #f2f4f7;
  color: #475467;
}

.conteudo-listagem__canal {
  display: inline-block;
  margin: 1px 4px 1px 0;
  padding: 1px 7px;
  border: 1px solid #d0d5dd;
  border-radius: 10px;
  font-size: 11px;
  color: #475467;
  white-space: nowrap;
}

.conteudo-listagem__status {
  display: inline-block;
  padding: 3px 10px;
  border-radius: 12px;
  font-size: 11px;
  font-weight: 600;
  color: #fff;
  white-space: nowrap;
  line-height: 1.4;
}

.conteudo-listagem__empty {
  text-align: center;
  padding: 48px 20px !important;
  color: #98a2b3;
}

.conteudo-listagem__empty-icon {
  color: #d0d5dd;
  margin-bottom: 8px;
}

.conteudo-listagem__empty p {
  font-size: 13px;
  margin: 0;
}

@media (max-width: 768px) {
  .conteudo-listagem__header {
    flex-direction: column;
    align-items: flex-start;
  }
}
</style>
